import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { movementsApi } from '@/api/endpoints'
import type {
  DigitooSendRequest,
  DigitooSendResult,
  Direction,
  ExportFilter,
  MovementPage,
  MovementSort,
  MovementStats,
  MovementStatus,
} from '@/api/types'
import { Button, Card, Field, Input, Select } from '@/components/ui'
import StatusBadge, { ExportBadge } from '@/components/StatusBadge'
import DigitooExportDialog from '@/components/DigitooExportDialog'
import { useListStore } from '@/stores/useListStore'
import { errorMessage } from '@/utils/errors'
import { formatDate, formatMoney, STATUS_LABELS } from '@/utils/format'

const PAGE_SIZE = 50

const TABS: { key: MovementStatus | 'ALL'; label: string }[] = [
  { key: 'MISSING', label: STATUS_LABELS.MISSING },
  { key: 'UNTRACEABLE', label: STATUS_LABELS.UNTRACEABLE },
  { key: 'SUPPLIED', label: STATUS_LABELS.SUPPLIED },
  { key: 'NOT_NEEDED', label: STATUS_LABELS.NOT_NEEDED },
  { key: 'ALL', label: 'Vše' },
]

/**
 * Seznam pohybů. Filtry žijí v URL, takže se na výběr dá vrátit z detailu
 * i tlačítkem Zpět a dá se poslat odkazem.
 */
export default function MovementsPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const setList = useListStore((s) => s.setList)

  const status = (params.get('status') ?? 'MISSING') as MovementStatus | 'ALL'
  const q = params.get('q') ?? ''
  const direction = (params.get('direction') ?? '') as Direction | ''
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const importId = params.get('importId') ?? ''
  const exportFilter = (params.get('export') ?? '') as ExportFilter | ''
  const sort = (params.get('sort') ?? 'DATE_DESC') as MovementSort
  const page = Number(params.get('page') ?? 0)

  const [query, setQuery] = useState(q)
  const [data, setData] = useState<MovementPage | null>(null)
  const [stats, setStats] = useState<MovementStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [reload, setReload] = useState(0)
  const lastParams = useRef<string | null>(null)

  // Výběr pro export. `allMatching` = všechno, co odpovídá filtru (i na
  // dalších stránkách) — pak se backendu posílá filtr, ne seznam id.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [allMatching, setAllMatching] = useState(false)
  const [exporting, setExporting] = useState<Pick<DigitooSendRequest, 'ids' | 'filter'> | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const filter = useMemo(
    () => ({
      q: q || undefined,
      status: status === 'ALL' ? undefined : status,
      direction: direction || undefined,
      from: from || undefined,
      to: to || undefined,
      importId: importId || undefined,
      export: exportFilter || undefined,
    }),
    [q, status, direction, from, to, importId, exportFilter],
  )
  const filterKey = JSON.stringify(filter)

  // Jiný filtr = jiný výběr; listování stránkami výběr nechává.
  useEffect(() => {
    setSelected(new Set())
    setAllMatching(false)
  }, [filterKey])

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)))
    if (!('page' in patch)) next.delete('page')
    setParams(next, { replace: true })
  }

  // Hledání se pouští s malým zpožděním, ať se nedotazujeme na každé písmeno.
  useEffect(() => {
    if (query === q) return
    const t = setTimeout(() => update({ q: query }), 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  useEffect(() => {
    let cancelled = false
    // Obnovení stejného výběru (fronta Digitoo) jde potichu, bez probliknutí.
    const silent = lastParams.current === params.toString()
    lastParams.current = params.toString()
    if (!silent) setLoading(true)
    movementsApi
      .search({
        ...filter,
        sort,
        page,
        size: PAGE_SIZE,
      })
      .then((res) => {
        if (cancelled) return
        setData(res)
        setError(null)
        setList(res.items.map((i) => i.id), params.toString())
      })
      .catch((e) => !cancelled && setError(errorMessage(e, 'Seznam se nepodařilo načíst.')))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, reload])

  useEffect(() => {
    movementsApi.stats().then(setStats).catch(() => undefined)
  }, [])

  // Dokud něco na stránce čeká ve frontě, obnovujeme — štítek se pak sám
  // přepne na „Exportováno“.
  const pending = data?.items.some((i) => i.exportState === 'PENDING')
  useEffect(() => {
    if (!pending) return
    const t = setTimeout(() => setReload((r) => r + 1), 4000)
    return () => clearTimeout(t)
  }, [pending, data])

  const pageIds = data?.items.map((i) => i.id) ?? []
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const selectedCount = allMatching ? (data?.total ?? 0) : selected.size

  const toggle = (id: string) => {
    setAllMatching(false)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const togglePage = () => {
    setAllMatching(false)
    setSelected((prev) => {
      const next = new Set(prev)
      pageIds.forEach((id) => (pageAllSelected ? next.delete(id) : next.add(id)))
      return next
    })
  }

  const clearSelection = () => {
    setSelected(new Set())
    setAllMatching(false)
  }

  const onQueued = (r: DigitooSendResult) => {
    setExporting(null)
    clearSelection()
    setNotice(`${r.fileCount} dokladů zařazeno k odeslání do Digitoo.`)
    setReload((x) => x + 1)
  }

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1
  const hasFilters = q || direction || from || to || importId || exportFilter

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Doklady k bankovním pohybům</h1>
        {stats?.counts.ALL === 0 && (
          <Link to="/import" className="text-sm text-brand-600 hover:underline">
            Zatím tu nic není — naimportuj XML z FlexiBee →
          </Link>
        )}
      </div>

      <div className="-mx-4 overflow-x-auto px-4">
        <div className="flex w-max gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => update({ status: t.key === 'MISSING' ? '' : t.key })}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                status === t.key ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {t.label}
              {stats && (
                <span className={`ml-2 text-xs ${status === t.key ? 'text-brand-100' : 'text-slate-400'}`}>
                  {stats.counts[t.key] ?? 0}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <Card className="p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr]">
          <Field label="Hledat">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="firma, popis, VS, kód, částka…"
              autoFocus
            />
          </Field>
          <Field label="Směr">
            <Select value={direction} onChange={(e) => update({ direction: e.target.value })}>
              <option value="">Příjmy i výdaje</option>
              <option value="OUT">Výdaje</option>
              <option value="IN">Příjmy</option>
            </Select>
          </Field>
          <Field label="Digitoo">
            <Select value={exportFilter} onChange={(e) => update({ export: e.target.value })}>
              <option value="">Vše</option>
              <option value="NOT_EXPORTED">Neexportované</option>
              <option value="EXPORTED">Exportované</option>
            </Select>
          </Field>
          <Field label="Od">
            <Input type="date" value={from} onChange={(e) => update({ from: e.target.value })} />
          </Field>
          <Field label="Do">
            <Input type="date" value={to} onChange={(e) => update({ to: e.target.value })} />
          </Field>
          <Field label="Řazení">
            <Select value={sort} onChange={(e) => update({ sort: e.target.value === 'DATE_DESC' ? '' : e.target.value })}>
              <option value="DATE_DESC">Nejnovější</option>
              <option value="DATE_ASC">Nejstarší</option>
              <option value="AMOUNT_DESC">Největší částka</option>
              <option value="AMOUNT_ASC">Nejmenší částka</option>
            </Select>
          </Field>
        </div>
        {hasFilters && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            {importId && <span className="rounded bg-brand-50 px-2 py-0.5 text-brand-700">jen z vybraného importu</span>}
            <button
              className="text-brand-600 hover:underline"
              onClick={() => {
                setQuery('')
                setParams(status === 'MISSING' ? {} : { status }, { replace: true })
              }}
            >
              zrušit filtry
            </button>
          </div>
        )}
      </Card>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {notice && (
        <div className="flex items-center justify-between rounded-lg bg-green-50 px-4 py-2 text-sm text-green-800">
          <span>{notice}</span>
          <button className="text-green-700 hover:underline" onClick={() => setNotice(null)}>
            zavřít
          </button>
        </div>
      )}

      {pageAllSelected && data && data.total > pageIds.length && (
        <div className="rounded-lg bg-brand-50 px-4 py-2 text-center text-sm text-brand-800">
          {allMatching ? (
            <>
              Vybráno všech <strong>{data.total}</strong> pohybů odpovídajících filtru.{' '}
              <button className="font-medium hover:underline" onClick={clearSelection}>
                Zrušit výběr
              </button>
            </>
          ) : (
            <>
              Vybráno {pageIds.length} pohybů na této stránce.{' '}
              <button className="font-medium hover:underline" onClick={() => setAllMatching(true)}>
                Vybrat všech {data.total} odpovídajících filtru
              </button>
            </>
          )}
        </div>
      )}

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase text-slate-400">
                <th className="w-10 py-2 pl-4">
                  <input
                    type="checkbox"
                    aria-label="Vybrat vše na stránce"
                    checked={pageAllSelected}
                    onChange={togglePage}
                    disabled={!pageIds.length}
                  />
                </th>
                <th className="px-4 py-2">Datum</th>
                <th className="px-4 py-2">Firma / popis</th>
                <th className="px-4 py-2">VS</th>
                <th className="px-4 py-2 text-right">Částka</th>
                <th className="px-4 py-2">Stav</th>
                <th className="px-4 py-2 text-center">Doklady</th>
              </tr>
            </thead>
            <tbody className={loading ? 'opacity-50' : ''}>
              {data?.items.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => navigate(`/doklady/${m.id}`)}
                  className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${
                    allMatching || selected.has(m.id) ? 'bg-brand-50/60' : ''
                  }`}
                >
                  <td className="py-2.5 pl-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label="Vybrat"
                      checked={allMatching || selected.has(m.id)}
                      onChange={() => toggle(m.id)}
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <div>{formatDate(m.issuedOn)}</div>
                    <div className="text-xs text-slate-400">{m.kod}</div>
                  </td>
                  <td className="max-w-md px-4 py-2.5">
                    <div className="truncate font-medium">{m.companyName ?? <span className="text-slate-400">bez firmy</span>}</div>
                    <div className="truncate text-xs text-slate-500">{m.description}</div>
                    {m.note && <div className="truncate text-xs italic text-amber-700">{m.note}</div>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{m.varSym}</td>
                  <td
                    className={`whitespace-nowrap px-4 py-2.5 text-right font-medium tabular-nums ${
                      m.direction === 'OUT' ? 'text-red-700' : m.direction === 'IN' ? 'text-green-700' : ''
                    }`}
                  >
                    {formatMoney(m.amount, m.currency, m.direction)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-col items-start gap-1">
                      <StatusBadge status={m.status} />
                      <ExportBadge state={m.exportState} />
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-center text-slate-500">{m.fileCount || ''}</td>
                </tr>
              ))}
              {data && data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                    {status === 'MISSING' && !hasFilters ? 'Žádný chybějící doklad 🎉' : 'Nic nenalezeno'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)} z {data.total}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" disabled={page === 0} onClick={() => update({ page: String(page - 1) })}>
              ← Předchozí
            </Button>
            <Button
              variant="secondary"
              disabled={page + 1 >= pages}
              onClick={() => update({ page: String(page + 1) })}
            >
              Další →
            </Button>
          </div>
        </div>
      )}
      {data && data.total <= PAGE_SIZE && data.total > 0 && (
        <p className="text-sm text-slate-400">{data.total} pohybů</p>
      )}

      {selectedCount > 0 && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-200 bg-white px-4 py-3 shadow-lg">
          <span className="text-sm">
            Vybráno <strong>{selectedCount}</strong> pohybů
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={clearSelection}>
              Zrušit výběr
            </Button>
            <Button onClick={() => setExporting(allMatching ? { filter } : { ids: [...selected] })}>
              Exportovat do Digitoo
            </Button>
          </div>
        </div>
      )}

      {exporting && (
        <DigitooExportDialog selection={exporting} onClose={() => setExporting(null)} onQueued={onQueued} />
      )}
    </div>
  )
}
